import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import classNames from 'classnames';
import Container from '@helios/Container';
import PageIntro from '@helios/PageIntro';
import Prose from '@helios/Prose';
import Layout from '@/components/Layout.js';
import CTA from '@/components/CTA.js';
import ApplyNow from '@/components/ApplyNow.js';
import { allSites } from '@/utils/domains.js';
import styles from '@/styles/OnlineCommunityCollegePage.module.scss';
import StudentJosephine from '@/public/images/occ-student-josephine.jpg';
import ACCJC from '@/public/images/accjc.svg';
import LiveClasses from '@/public/images/occ-live-classes.jpg';
import StudentResearching from '@/public/images/occ-student-researching.jpg';
import FAQAccordion from '@helios/FAQAccordion';
import FAQSection from '@helios/FAQSection';
import Section from '@helios/Section';
import SiteLink from '@/components/SiteLink';

// The article images fill the text column: Container sm (848px with its
// gutters: 20px a side below 768px, 32px to 1199px, 64px from 1200px).
const ARTICLE_IMAGE_SIZES = '(max-width: 767px) calc(100vw - 40px), (max-width: 847px) calc(100vw - 64px), (max-width: 1199px) 784px, 720px';
export const getStaticPaths = async () => {
  return {
    paths: allSites.map(site => ({
      params: {
        site
      }
    })),
    fallback: false
  };
};
export async function getStaticProps({
  params
}) {
  return {
    props: {
      site: params.site
    }
  };
}
function OnlineCommunityCollege(t0) {
  const $ = _c(114);
  const {
    site
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Head><title>Online Community College | Campus.edu</title><link rel="canonical" href="https://campus.edu/online-community-college" /><meta httpEquiv="X-UA-Compatible" content="IE=Edge" /><meta name="description" content="Explore the ultimate guide to online community college and learn about accreditation, financial aid, resources, and how to find the right program." /><meta name="keywords" content="Online Community College" /><meta name="viewport" content="width=device-width, initial-scale=1" /></Head>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Link href="/.." target="_blank" rel="noreferrer">college</Link>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Section><Container width="sm"><div className={styles.intro}><PageIntro title="Online Community College"><blockquote className={styles.lede}><i>Campus is the new alternative to traditional{" "}{t2}. We're on a mission to maximize access to a world-class education, starting with our groundbreaking{" "}<Link href="/../online-associates-degree-in-business" target="_blank" rel="noreferrer">online associate degree in business program</Link>. Our program features live, face-to-face classes led by professors who also teach at top-ranked universities including Princeton, UC Berkeley, UT Austin, and Howard.</i></blockquote></PageIntro></div></Container></Section>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p>Traditionally, community colleges have offered associate degrees and workforce certifications, served local populations, and created connections between education and employment in a specific geographic area.</p>;
    t5 = <p>However, with the rise of online education, community colleges have expanded their reach and are creating new opportunities for like-minded students across the country.</p>;
    t6 = <p>In fact, remote learning has rapidly grown in popularity within the last decade, and the global pandemic in 2020 solidified both the need for and legitimacy of online education.</p>;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
  }
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  let t30;
  let t7;
  let t8;
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p>In 2022,{" "}<a href="https://hechingerreport.org/proof-points-most-college-kids-are-taking-at-least-one-class-online-even-long-after-campuses-reopened/" target="_blank" rel="noreferrer">more than 50% of students</a>{" "}took at least one class toward their degree online, and that number is expected to increase. As students' desire to learn online grows, institutions like Campus are working to meet that demand by offering flexible, accredited online degree and certificate programs that help students meet their education and career goals.</p>;
    t8 = <p>Flexibility isn't the only feature of online education that makes it appealing for some students.</p>;
    t9 = <p>Online community college programs can also be more affordable than traditional, on-campus programs, because tuition tends to be lower, and students can save on other costs like housing and commuting.</p>;
    t10 = <p>Overall accessibility is a major reason students choose online programs. An online education format allows students to access classes, course materials, and academic support anywhere, at almost any time.</p>;
    t11 = <p className={styles["paragraph-end"]}>In short, online community colleges are instrumental in driving some of the most disruptive, positive changes in higher education today.</p>;
    t12 = <h2>Why choose an online community college?</h2>;
    t13 = <p>Below are some reasons why students may choose an online community college vs. attending college in person.</p>;
    t14 = <h3>Flexibility for busy lifestyles</h3>;
    t15 = <p>Online community colleges offer students the opportunity to get a valuable, rigorous education without neglecting their other priorities.</p>;
    t16 = <p>An online community college can be an ideal fit for students who work part- or full-time, or for students who have family commitments. Online community colleges tend to offer courses during the evening or even on weekends, so students can generally stay employed or perform caregiving activities as they earn their degrees.</p>;
    t17 = <h3>Cost-effective</h3>;
    t18 = <p>Costs can vary widely, even among community colleges, but a fully online program can help students save significantly on costs associated with tuition and commuting. The convenience of online learning can lead to savings in terms of time, allowing students to work more while they study (versus having to attend classes in person), making it a smart choice for many individuals seeking affordable education options.</p>;
    t19 = <p>There are also factors that students may not think about upfront, such as the cost of textbooks, which online community college programs may offer virtually and a lower cost. At Campus, for example, online business students have access to all textbooks and course materials at no additional cost.</p>;
    t20 = <h3>Accelerated programs and shorter terms</h3>;
    t21 = <p>Term schedules are another reason students might choose an online community college. Traditional, on-campus programs tend to offer courses on a semester schedule. These schools usually offer two semesters a year, and each semester is about 15 to 16 weeks long. Within this kind of format, there's not much students can do to accelerate the pace of their degree.</p>;
    t22 = <p>It's important to note that not all classes are offered every semester, either, so students sometimes have to wait two or even three semesters before they can enroll in a class they need to graduate.</p>;
    t23 = <p>In contrast, online community college programs are more likely to offer accelerated program timelines. Terms might be 8 or 10 weeks, or an online school might offer three terms a year rather than two. So, if you're hoping to earn your degree fairly quickly, you may want to consider online community college programs.</p>;
    t24 = <div className={classNames(styles.media, styles["media--end"])}><Image src={StudentJosephine} alt="A student participating in an online community college class" className={styles["media-image"]} sizes={ARTICLE_IMAGE_SIZES} /></div>;
    t25 = <h2>How to choose the right online community college</h2>;
    t26 = <p>If you're considering enrolling in an online community college, here are a few key things you should consider.</p>;
    t27 = <h3>1. Is the school accredited?</h3>;
    t28 = <p>Essentially, accreditation serves as a quality check: during the accreditation process, a third party reviews a school's administration, academic programs, faculty, support, and student outcomes. Accreditation exists to protect students from predatory or financially unstable institutions, and it also ensures that schools are offering thoughtful, well-designed programs that provide value to students.</p>;
    t29 = <p>It's also an important part of transfer agreements because most accredited institutions will not accept transfer credits from unaccredited ones.</p>;
    t30 = <p>So, how do you know if a school is accredited?</p>;
    $[6] = t10;
    $[7] = t11;
    $[8] = t12;
    $[9] = t13;
    $[10] = t14;
    $[11] = t15;
    $[12] = t16;
    $[13] = t17;
    $[14] = t18;
    $[15] = t19;
    $[16] = t20;
    $[17] = t21;
    $[18] = t22;
    $[19] = t23;
    $[20] = t24;
    $[21] = t25;
    $[22] = t26;
    $[23] = t27;
    $[24] = t28;
    $[25] = t29;
    $[26] = t30;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t10 = $[6];
    t11 = $[7];
    t12 = $[8];
    t13 = $[9];
    t14 = $[10];
    t15 = $[11];
    t16 = $[12];
    t17 = $[13];
    t18 = $[14];
    t19 = $[15];
    t20 = $[16];
    t21 = $[17];
    t22 = $[18];
    t23 = $[19];
    t24 = $[20];
    t25 = $[21];
    t26 = $[22];
    t27 = $[23];
    t28 = $[24];
    t29 = $[25];
    t30 = $[26];
    t7 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  let t31;
  let t32;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <p>Institutions usually make their accreditation visible on their website, but you can also verify a school's accreditation through the U.S. Department of Education's{" "}<a href="https://ope.ed.gov/dapip/#/home" target="_blank" rel="noreferrer">Database of Accredited Postsecondary Institutions and Programs</a>{"."}</p>;
    t32 = <p>While there are several accreditation types, if you're attending a degree-focused program, you'll want to look for a school with regional (preferred) or national accreditation. Campus, for example, is regionally accredited by the Accrediting Commission for Community and Junior Colleges (ACCJC).</p>;
    $[30] = t31;
    $[31] = t32;
  } else {
    t31 = $[30];
    t32 = $[31];
  }
  let t33;
  let t34;
  let t35;
  let t36;
  let t37;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <div className={styles.media}><Image src={ACCJC} alt="Logo for the Accrediting Commission for Community and Junior Colleges, the regional accreditor that provides accreditation to Campus.edu, an online community college" className={styles["media-logo"]} sizes="221px" /></div>;
    t34 = <h3>2. How does the school structure its online programs?</h3>;
    t35 = <p>Many colleges offer online learning experiences, but there are a few key differences in how programs are built and delivered. Specifically, you'll want to research the following things:</p>;
    t36 = <li><p><b><i>Degrees and certificates:</i></b>{" "}Certificate programs are more specific to a particular field of expertise, while a degree may give you a more well-rounded education. Not every online community college offers the same degree and certificate programs, so research schools that align well with your academic and professional goals.</p></li>;
    t37 = <b><i>Synchronous vs. asynchronous learning options:</i></b>;
    $[32] = t33;
    $[33] = t34;
    $[34] = t35;
    $[35] = t36;
    $[36] = t37;
  } else {
    t33 = $[32];
    t34 = $[33];
    t35 = $[34];
    t36 = $[35];
    t37 = $[36];
  }
  let t38;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <p>{t37}{" "}Synchronous online classes allow you to complete your coursework online and in real time. Synchronous classes are an ideal option for students who want the chance to interact live with peers and professors, but they may not be the best choice for students with unpredictable work schedules or caregiving duties. The Campus{" "}<Link href="/online-associates-degree-in-business" target="_blank" rel="noreferrer">online associate degree in business</Link>{" "}program consists of synchronous online classes so that students can meet live and directly engage with professors and teaching assistants. Classes are generally scheduled in the evenings so that many students who are working full-time or part-time can continue to do so while earning their degrees.</p>;
    $[37] = t38;
  } else {
    t38 = $[37];
  }
  let t39;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <ul>{t36}<li>{t38}<p>Asynchronous classes <i>don't</i>{" require you to meet online on a specific day and time. You'll still need to meet assignment due dates, but your interactions with peers and professors will usually be through discussion boards or similar tools. Your professor may ask you to schedule a virtual meeting once or twice during the term, but you'll likely have flexibility in determining a meeting time."}</p><p>If you're hoping to build a strong sense of online community through real-time chats with classmates and faculty, asynchronous learning may not be the best choice. However, if you can't commit to meeting on specific days and times, an asynchronous option could be a good fit.</p></li></ul>;
    $[38] = t39;
  } else {
    t39 = $[38];
  }
  let t40;
  let t41;
  let t42;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <div className={styles.media}><Image src={LiveClasses} alt="A professor at Campus leading a live, online community college class" className={styles["media-image"]} sizes={ARTICLE_IMAGE_SIZES} /></div>;
    t41 = <h3>3. What kind of support is available?</h3>;
    t42 = <p>To learn more about academic support offerings outside the classroom, review the school's website carefully or contact an enrollment specialist or advisor at your prospective school. Be sure to ask about advising processes, individual or group tutoring programs, and career transition services. At Campus, business students have access to support services such as on-demand writing and math tutoring, 24/7 mental and emotional wellness counseling, and a success coach.</p>;
    $[39] = t40;
    $[40] = t41;
    $[41] = t42;
  } else {
    t40 = $[39];
    t41 = $[40];
    t42 = $[41];
  }
  let t43;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <Link href="/professors" target="_blank" rel="noreferrer">faculty bios</Link>;
    $[42] = t43;
  } else {
    t43 = $[42];
  }
  let t44;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <Link href="/professors" target="_blank" rel="noreferrer">Campus professors</Link>;
    $[43] = t44;
  } else {
    t44 = $[43];
  }
  let t45;
  let t46;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <Section><Container width="sm"><Prose size="article">{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t24}{t25}{t26}{t27}{t28}{t29}{t30}{t31}{t32}{t33}{t34}{t35}{t39}{t40}{t41}{t42}<p>Don't forget about the support you get in the virtual classroom, too. Take some time to also review{" "}{t43}{" "}– those can help you better understand who you might be learning from. It's typical for faculty members in an academic program to have at least a Master's degree in the subject they're teaching, but you might want to check to see if they have any publications, awards, or unique professional experiences. Who you learn from can heavily impact your student experience – that's why{" "}{t44}{" "}in our online business program are experts in their fields who also teach at colleges like{" "}<Link href="/professors/thomas-hitchner" target="_blank" rel="noreferrer">UCLA</Link>, UT Austin, and Howard.</p><h3>4. What are the student outcomes like?</h3><p>Finally, don't forget to ask your prospective schools for information about student outcomes. For example, you'll want to know what percentage of enrolled students graduate and how long it takes them to do so.</p><p>Schools should also be able to give you information about their transfer rates, including the average number of credits students transfer with.</p><p>It's also good practice to ask schools for information about their job placement rates and the average income their graduates earn. Sometimes it's more difficult to get this information, but schools that are successful in helping students find lucrative employment opportunities are generally eager to share those statistics.</p></Prose></Container></Section>;
    t46 = <CTA surface="subtle" />;
    $[44] = t45;
    $[45] = t46;
  } else {
    t45 = $[44];
    t46 = $[45];
  }
  let t47;
  let t48;
  let t49;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <h2>Top community college programs 2024</h2>;
    t48 = <p>There's no shortage of great community colleges across the U.S., but there are a few whose programs stand out for their quality, innovation, and commitment to student success.</p>;
    t49 = <Link href="/.." target="_blank" rel="noreferrer">Campus</Link>;
    $[46] = t47;
    $[47] = t48;
    $[48] = t49;
  } else {
    t47 = $[46];
    t48 = $[47];
    t49 = $[48];
  }
  let t50;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <Link href="/online-associates-degree-in-business" target="_blank" rel="noreferrer">online associate degree in business</Link>;
    $[49] = t50;
  } else {
    t50 = $[49];
  }
  let t51;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <Link href="/blog/financial-aid/who-is-eligible-for-a-pell-grant" target="_blank" rel="noreferrer">Pell Grant</Link>;
    $[50] = t51;
  } else {
    t51 = $[50];
  }
  let t52;
  let t53;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <li><p>{t49}{" "}offers a fully online associate degree program taught by professors who also teach at some of the top universities in the country. The Campus{" "}{t50}{" "}program prepares students for careers or further study in entrepreneurship, marketing, communication, business administration, and other in-demand fields. Because the program tuition cost falls below the maximum{" "}{t51}, many students are able to attend with no out-of-pocket tuition costs. The{" "}<Link href="/experience" target="_blank" rel="noreferrer">Campus experience</Link>{" "}also includes access to required course materials at no additional cost, free math and writing tutoring, and a student success coach from day one through graduation.</p></li>;
    t53 = <li><p><b>SUNY ASAP</b>{" is an innovative program that helps economically disadvantaged students earn an associate degree at little-to-no cost within three years. Students enrolled in the SUNY ASAP program receive support throughout their college journey, including a dedicated academic advisor, early registration, and even food and gas cards to help offset the costs that can come with attending classes."}</p></li>;
    $[51] = t52;
    $[52] = t53;
  } else {
    t52 = $[51];
    t53 = $[52];
  }
  let t54;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <ul>{t52}{t53}<li><p>Though <b>Miami-Dade College</b>{" offers bachelor's programs, their associate degree and certificate programs deserve special recognition for their breadth, depth, and transferability. Miami-Dade is the largest degree-granting institution in the nation with one of the most diverse populations, so the administration and faculty work hard to create opportunities for connection and collaboration among students."}</p></li><li><p><b>Dallas College</b>{", which comprises seven community college campuses in the Dallas area, takes a holistic approach to student success. Generous financial aid policies and scholarship opportunities keep classes affordable, and enrolled students have access to academic, health, wellness, and employment support services, including Dallas College's no-cost WorkReadyU program, which prepares students for college and career success."}</p></li></ul>;
    $[53] = t54;
  } else {
    t54 = $[53];
  }
  let t55;
  let t56;
  let t57;
  let t58;
  let t59;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <p>What do all of these programs have in common beyond their commitment to providing every student with a first-class educational experience? Members of the Campus{" "}<Link href="/about#leadership-and-governance" target="_blank" rel="noreferrer">Board of Trustees</Link>{" "}have led these trailblazing institutions and now use their passion and experience to help make Campus one of the most accessible, affordable, and inventive community college programs in the country.</p>;
    t56 = <div className={classNames(styles.media, styles["media--end"])}><Image src={StudentResearching} alt="A prospective student researching online community college options" className={styles["media-image"]} sizes={ARTICLE_IMAGE_SIZES} /></div>;
    t57 = <h2>Admission process and requirements</h2>;
    t58 = <p>Enrolling at most online community colleges is often very simple and straightforward. Unless you're participating in a dual enrollment program, you'll need to have earned either a high school diploma or a GED. If your grades in high school weren't the strongest, don't worry – most community colleges don't require you to have a specific GPA. If you have earned an exceptional GPA, however, don't hesitate to share it, as there may be scholarships and other programming that the college may share with you if they have this information.</p>;
    t59 = <p>You'll also likely need to submit proof of high school/GED completion, and you may be asked to fill out a short application with some basic personal and demographic information as well.</p>;
    $[54] = t55;
    $[55] = t56;
    $[56] = t57;
    $[57] = t58;
    $[58] = t59;
  } else {
    t55 = $[54];
    t56 = $[55];
    t57 = $[56];
    t58 = $[57];
    t59 = $[58];
  }
  let t60;
  let t61;
  let t62;
  let t63;
  let t64;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <p>Once your application has been reviewed, some schools will ask you to join them for a short interview or consultation to ensure that they're a good fit for you. At Campus, our{" "}<Link href="/admissions-application" target="_blank" rel="noreferrer">online admissions application</Link>{" "}can be completed in under 15 minutes and does not require any SAT or ACT test scores. We also require prospective students to schedule a short interview with an admissions counselor after submitting the application.</p>;
    t61 = <p>As part of your enrollment process, you may need to submit financial aid information. For federal financial aid opportunities, you'll need to fill out the FAFSA form and assemble any required supporting documents.</p>;
    t62 = <p>If you're applying for any institutional or private scholarship opportunities, you'll need to fill out any associated forms and complete any supplemental requirements, like essays or interviews.</p>;
    t63 = <p className={styles["paragraph-end"]}>Once you've been accepted to an online community college, you may also need to take placement tests for subjects like English and math so that your advisor can recommend the course that's best for you. It can be a good idea to brush up on any placement test topic areas before you begin your assessment, but remember that placement tests are designed to help you choose the class that's the best fit for your current knowledge and skill level.</p>;
    t64 = <h2>Financial aid and scholarships</h2>;
    $[59] = t60;
    $[60] = t61;
    $[61] = t62;
    $[62] = t63;
    $[63] = t64;
  } else {
    t60 = $[59];
    t61 = $[60];
    t62 = $[61];
    t63 = $[62];
    t64 = $[63];
  }
  let t65;
  let t66;
  let t67;
  let t68;
  let t69;
  let t70;
  let t71;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <p>If you have concerns about your ability to finance your education, it's worth{" "}<Link href="/blog/community-college/how-to-pay-for-community-college" target="_blank" rel="noreferrer">learning more about your funding options</Link>. One of the major benefits of learning online is that many of the costs associated with learning on-campus – such as housing, commuting, purchasing textbooks, and more – are reduced or eliminated.</p>;
    t66 = <p>The best way to educate yourself about how to pay for college is by initiating a conversation with your school's financial aid office.</p>;
    t67 = <p>One thing any financial aid officer will likely tell you is that financial aid comes in many forms, including:</p>;
    t68 = <ul><li>Loans</li><li>Grants</li><li>Scholarships</li><li>Work-study programs</li></ul>;
    t69 = <br />;
    t70 = <p>These resources may be available at the federal, state, institutional, or local level, so be sure to explore multiple avenues as you research your aid options.</p>;
    t71 = <p>The first step to finding out what Federal aid you're eligible for as an online community college student is to fill out and submit the FAFSA form. Once you submit your FAFSA form, a financial aid officer will review your application and give you information about any aid packages the school can offer.</p>;
    $[64] = t65;
    $[65] = t66;
    $[66] = t67;
    $[67] = t68;
    $[68] = t69;
    $[69] = t70;
    $[70] = t71;
  } else {
    t65 = $[64];
    t66 = $[65];
    t67 = $[66];
    t68 = $[67];
    t69 = $[68];
    t70 = $[69];
    t71 = $[70];
  }
  let t72;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = <Link href="/blog/community-college/free-community-college-guide" target="_blank" rel="noreferrer">free community college</Link>;
    $[71] = t72;
  } else {
    t72 = $[71];
  }
  let t73;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = <p>Be sure to do your research as far as specific opportunities your state may offer as well. Many states offer{" "}{t72}{" "}programs, so review programs in your state to see if you're eligible.{" "}<Link href="/tuition" target="_blank" rel="noreferrer">Tuition</Link>{" "}for the online associate in business program at Campus is below the Federal Pell Grant maximum, so students who qualify for the full Pell can attend without any out-of-pocket tuition costs. In fact, not a single student enrolled in our online AA in business program has had to take out any student loans to pay for program tuition.</p>;
    $[72] = t73;
  } else {
    t73 = $[72];
  }
  let t74;
  let t75;
  let t76;
  let t77;
  let t78;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <p className={styles["paragraph-end"]}>Scholarships can also help you reduce your out-of-pocket costs. To receive a scholarship, you may need to meet certain criteria or submit application materials, like essays or samples of your work. Check out our{" "}<Link href="/blog/community-college/can-you-get-scholarships-for-community-college" target="_blank" rel="noreferrer">guide to community college scholarships</Link>{" "}to learn more about how scholarships can help you pay for your education.</p>;
    t75 = <h2>Success strategies for online students</h2>;
    t76 = <p>Choosing an online community college can set you up for success, but enrolling in a virtual program is just the beginning. Follow these tips to make the most of your online experience.</p>;
    t77 = <h3>1. Manage your time.</h3>;
    t78 = <p>Earning your degree as an online student does mean you have more flexibility than students taking classes on-campus, but you'll need strong time management skills to succeed. Before each term, be sure to review your other commitments and estimate how many hours a week you can allocate to studying.</p>;
    $[73] = t74;
    $[74] = t75;
    $[75] = t76;
    $[76] = t77;
    $[77] = t78;
  } else {
    t74 = $[73];
    t75 = $[74];
    t76 = $[75];
    t77 = $[76];
    t78 = $[77];
  }
  let t79;
  let t80;
  let t81;
  let t82;
  let t83;
  let t84;
  let t85;
  let t86;
  let t87;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t79 = <p>Talk with your advisor or instructors about how many hours a week you should expect to prepare for class or complete assignments, and be sure that you don't sign up for more classes than you can allocate study time to. Campus{" "}<Link href="/experience" target="_blank" rel="noreferrer">student success coaches</Link>{" "}help students review their personal and academic schedules to ensure they can manage their time more effectively.</p>;
    t80 = <p>Once you've determined the right number of classes, make schoolwork part of your regular schedule. You don't necessarily need to study at the same time every day, but dedicate regular timeblocks during the week to school.</p>;
    t81 = <p>Finally, be sure to use the syllabus for each class to look ahead. Add your assignments for each class to the same calendar so that you can be aware of any particularly difficult weeks well in advance.</p>;
    t82 = <h3>2. Focus on the where and how.</h3>;
    t83 = <p>It's important to carve out time for yourself, but it also helps to think about where and how you study best. Do you prefer a calm, quiet space, or do you feel more energized in a busy coffee shop with music and conversation in the background? Does natural light help you stay awake, or does looking at a screen in a darkened room make you more comfortable?</p>;
    t84 = <p>Online learning should fit your life — but it does require the right tools. For students in the Campus online degree program who need them, we provide a laptop and Wi-Fi access at no additional cost, because we don't want technology access to stand between you and your degree.</p>;
    t85 = <h3>3. Stay engaged.</h3>;
    t86 = <p>Some students can find it a bit harder to stay engaged in a virtual class than a class that meets face-to-face. The key to succeeding in your online class is to bring the same energy to an online class as you would to an in-person one.</p>;
    t87 = <p>A lot of the time, when we're looking at screens, we're receiving information passively. When you're taking an online class, whether you're reading a text, viewing a video, or participating in a live online session, it's important to understand that you are a critical part of that interaction.</p>;
    $[78] = t79;
    $[79] = t80;
    $[80] = t81;
    $[81] = t82;
    $[82] = t83;
    $[83] = t84;
    $[84] = t85;
    $[85] = t86;
    $[86] = t87;
  } else {
    t79 = $[78];
    t80 = $[79];
    t81 = $[80];
    t82 = $[81];
    t83 = $[82];
    t84 = $[83];
    t85 = $[84];
    t86 = $[85];
    t87 = $[86];
  }
  let t88;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t88 = <i>want</i>;
    $[87] = t88;
  } else {
    t88 = $[87];
  }
  let t89;
  let t90;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t89 = <p>Your teachers {t88}{" you to ask questions and challenge assumptions. They want to know when you're excited about an idea, or if you get a chance to apply a theoretical concept in your daily life. Don't be afraid to"}{" "}<Link href="/blog/student-life/student-advice-online-community-college-classroom-tips" target="_blank" rel="noreferrer">connect with your peers</Link>, too. The stronger your online learning community is, the more likely you are to enjoy your classes and finish your program.</p>;
    t90 = <h3>4. Accept help.</h3>;
    $[88] = t89;
    $[89] = t90;
  } else {
    t89 = $[88];
    t90 = $[89];
  }
  let t91;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t91 = <p>Part of being an engaged online student is learning how to accept the support that's available to you. Online community colleges know that learning remotely can be challenging, and some institutions offer easily accessible tutoring through some type of academic support center or learning commons. Once you're a student at an institution, you'll be able to register online for a virtual appointment with a tutor at a time that's convenient for you, such as the{" "}<Link href="/experience" target="_blank" rel="noreferrer">free math and writing tutoring services</Link>{" "}offered in the Campus online business program.</p>;
    $[90] = t91;
  } else {
    t91 = $[90];
  }
  let t92;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t92 = <Section><Container width="sm"><Prose size="article">{t47}{t48}{t54}{t55}{t56}{t57}{t58}{t59}{t60}{t61}{t62}{t63}{t64}{t65}{t66}{t67}{t68}{t69}{t70}{t71}{t73}{t74}{t75}{t76}{t77}{t78}{t79}{t80}{t81}{t82}{t83}{t84}{t85}{t86}{t87}{t89}{t90}{t91}<p>Remember, too, that tutoring isn't the only type of support schools usually offer. Check with your school to see about online library, career, and{" "}<Link href="/blog/student-life/self-care-tips-online-classes-student-advice" target="_blank" rel="noreferrer">health and wellness</Link>{" "}resources available to you as a student.</p></Prose></Container></Section>;
    $[91] = t92;
  } else {
    t92 = $[91];
  }
  let t93;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t93 = <h2 className={styles["faq-title"]}>FAQs about online community college</h2>;
    $[92] = t93;
  } else {
    t93 = $[92];
  }
  let t94;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t94 = {
      question: "What is an online community college?",
      answer: <><p>An online community college gives students the opportunity to earn an associate degree or professional certificate virtually or in a hybrid learning environment. Online community colleges, just like traditional community colleges, usually offer a variety of academic and professional programs, and students can earn a degree or credential in nearly any discipline, including the arts, sciences, education, and business. Most online community colleges include access to robust support services for their students, including things like online tutoring and employment resources.</p></>
    };
    $[93] = t94;
  } else {
    t94 = $[93];
  }
  let t95;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t95 = {
      question: "How do online classes work at community colleges?",
      answer: <><p>The structure and format of an online community college class depend largely on the school and program.</p><p>If you're exploring an online community college, you'll want to ask the following questions:</p><ul><li><p><i>Is the class I'm considering synchronous or asynchronous?</i><br />Synchronous online classes meet in real time during specific days and times during a term. Asynchronous classes typically don't have any scheduled meeting times, so you can work through the course material when it's convenient for you.</p></li><li><p><i>How will I access my course materials?</i><br />Most community colleges rely on a learning management system, or LMS, to house resources like readings, videos, assignments, grades, and meeting links. If you've never used an LMS before, you may want to reach out to someone at the school you're considering and ask for any resources they may have that can help you get familiar with the layout and tools available to you.</p></li><li><p><i>What can I expect from the course?</i><br />If you're considering taking an online class, ask for a syllabus before you enroll. Reviewing a syllabus can help you understand how content is presented and how your learning will be assessed. Don't forget to review course policies regarding things like absences and late work, too.</p></li></ul></>
    };
    $[94] = t95;
  } else {
    t95 = $[94];
  }
  let t96;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t96 = {
      question: "Are online community college credits transferable?",
      answer: <><p>Online community college credits are generally easy to transfer, but you'll need to do some research before you enroll to better understand where and{" "}<Link href="/transfer">how your credits will transfer</Link>.</p><p>Most online community college programs have articulation agreements with four-year institutions. Articulation agreements describe exactly how many credits a course is equal to at the receiving institution, as well as what requirement in a program that course completes (e.g., whether a course counts as a general education elective, arts and humanities credit, etc.).</p><p>Ask your advisor for information about articulation agreements before you enroll to ensure you don't take classes that won't count toward your four-year degree if that's your eventual goal.</p></>
    };
    $[95] = t96;
  } else {
    t96 = $[95];
  }
  let t97;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t97 = {
      question: "What programs and degrees can I pursue at an online community college?",
      answer: <><p>Online community colleges offer a wide variety of associate degrees that can help you establish a career or accelerate your timeline to your bachelor's degree. Popular online community college degrees include the following:</p><ul><li><Link href="/online-associates-degree-in-business" target="_blank">A.A. in Business Administration</Link></li><li>A.A.S. in Accounting</li><li>A.A.S. in Criminal Justice</li><li><Link href="/online-degree-in-information-technology" target="_blank">A.S. Information Technology</Link></li><li><Link href="/online-paralegal-degree" target="_blank" rel="noreferrer">A.A. Paralegal Studies</Link></li><li>A.A. in General Education</li></ul><p>Online community colleges offer career-boosting certificates, too:</p><ul><li>AWS Cloud Solutions Architect</li><li><SiteLink site="sacramento" path="/programs/paul-mitchell/cosmetology-and-barbering" target="_blank" rel="noreferrer">Barbering and Cosmetology License</SiteLink></li><li><SiteLink site="sacramento" path="/programs/healthcare/medical-assisting-with-phlebotomy-certificate" target="_blank" rel="noreferrer">Medical Assistant with Phlebotomy</SiteLink></li><li><SiteLink site="sacramento" path="/programs/healthcare/medical-billing-and-coding-professional" target="_blank" rel="noreferrer">Medical Billing and Coding Professional</SiteLink></li><li>Technical Support Specialist</li></ul></>
    };
    $[96] = t97;
  } else {
    t97 = $[96];
  }
  let t98;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t98 = {
      question: "How do I apply to an online community college?",
      answer: <><p>Most schools will ask you to complete a short application form and submit a high school diploma or GED. If you're applying for financial aid, you'll also need to submit your FAFSA form and any supplemental materials required for institutional scholarships, if applicable. Some schools may invite you to meet with an admissions counselor for additional support during the enrollment process. Once you've been accepted, you'll need to take any required placement tests, and then you'll be able to register for classes.</p></>
    };
    $[97] = t98;
  } else {
    t98 = $[97];
  }
  let t99;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t99 = {
      question: "What are the tuition and fees for online community college courses?",
      answer: <><p>While the cost of community college varies by state, the National Center for Education Statistics reports that the average public community college costs a little less than four thousand dollars per year. Additional fees can add up to a few hundred to a few thousand dollars, depending on the school and program a student is pursuing. For more information about the cost of community college,{" "}<Link href="/blog/community-college/how-much-is-community-college" target="_blank" rel="noreferrer">check out our blog</Link>.</p></>
    };
    $[98] = t99;
  } else {
    t99 = $[98];
  }
  let t100;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t100 = {
      question: "Is financial aid available for online community college students?",
      answer: <><p>Yes, online community college students can generally apply for and receive financial aid. Research funding opportunities at the federal, state, and local levels to find opportunities you're eligible for, or chat with a financial aid officer at any prospective school for more information about your options.</p></>
    };
    $[99] = t100;
  } else {
    t100 = $[99];
  }
  let t101;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t101 = {
      question: "How do I know if an online community college is accredited?",
      answer: <><p>Most accredited schools will make this visible on their website or marketing materials, but you can always double-check a school's accreditation using the{" "}<a href="https://ope.ed.gov/dapip/#/home" target="_blank" rel="noreferrer">Database of Accredited Postsecondary Institutions and Programs</a>{"."}</p></>
    };
    $[100] = t101;
  } else {
    t101 = $[100];
  }
  let t102;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t102 = {
      question: "What support services are available for online students?",
      answer: <><p>Online community college students have access to many of the same support services students taking classes in-person enjoy. Schools can assign you an advisor who will provide you with guidance on course enrollment, graduation, and transferring.</p><p>Most online schools have robust virtual tutoring programs that allow you to get help in almost any subject area, either individually or in a small group. You can expect access to a wide variety of library resources, too – most schools belong to consortiums designed to increase virtual access to print resources, so if you're concerned about not being able to access a particular text, just reach out to your school's library team.</p><p>Finally, online community colleges know that students rely on technology to be successful, so you'll usually be able to connect with IT support personnel who can help answer any questions about passwords, your learning management system, or specific software and hardware requirements.</p></>
    };
    $[101] = t102;
  } else {
    t102 = $[101];
  }
  let t103;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t103 = {
      question: "Can I work while completing my online community college program?",
      answer: <><p>One of the primary reasons students enroll in online community college is because they can work part- or even full-time while they complete their degree. Most online community colleges are created with adult learners in mind.</p><p>Any synchronous classes are usually held during evenings or weekends, and many colleges also offer asynchronous course options.</p><p>Faculty members at online community colleges realize that their students may be working professionals, so course syllabi and assignment schedules are designed with an understanding that students are balancing many priorities.</p></>
    };
    $[102] = t103;
  } else {
    t103 = $[102];
  }
  let t104;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t104 = {
      question: "What are the technical requirements for taking online classes?",
      answer: <><p>While you don't necessarily need an expensive technical set-up to succeed in online classes, you should ensure you have the following things:</p><ul><li>Access to a laptop or desktop computer in case you are asked to use tools or programs that are not fully compatible with a mobile device</li><li>A reliable internet connection that supports video streaming</li><li>A webcam and microphone for virtual classes, conferences, tutoring, or advising</li></ul><p>Ask an admissions counselor about any institution-specific technology requirements, and be sure to check your syllabus at the beginning of each course for any tools or programs required by your professor.</p></>
    };
    $[103] = t104;
  } else {
    t104 = $[103];
  }
  let t105;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t105 = {
      question: "How do online exams and assessments work?",
      answer: <><p>Instructors at online community colleges assess students' work in a number of different ways. For example, you might be expected to respond in writing to questions on a discussion board or journal tool, or you might take a multiple-choice lesson comprehension quiz online. Some quizzes and exams may be open-book or open-note and may not be timed, while others may require you to complete all questions within a time limit.</p><p>Depending on the learning management system your school uses, quiz and exam security tools may vary. In some classes, you might be required to use a tool that locks your browser down while you complete a test, or you may be required to use your webcam and allow an online proctor to supervise you while you take a test.</p></>
    };
    $[104] = t105;
  } else {
    t105 = $[104];
  }
  let t106;
  let t107;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t106 = <p>Students at Campus have some incredibly inspiring stories, and we love featuring them on our blog. Read about some of their achievements and stories here:</p>;
    t107 = <ul><li><Link href="/blog/student-life/campus-student-feature-marquise-west" target="_blank">Marquise West</Link>, who didn't find a good fit at his local community college, but excelled online at Campus and started his career as an entrepreneur</li><li><Link href="/blog/student-life/campus-student-feature-jamora-williams-first-generation-college-student" target="_blank">Jamora Williams</Link>, a first-generation student who overcame a difficult past to be the first in her family to work toward earning a degree</li><li><Link href="/blog/student-life/campus-student-feature-deva-ramireddy" target="_blank">Deva Ramireddy</Link>, who suffered a major injury but was able to keep working toward his goals through remote learning</li></ul>;
    $[105] = t106;
    $[106] = t107;
  } else {
    t106 = $[105];
    t107 = $[106];
  }
  let t108;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t108 = <FAQSection><Container width="sm">{t93}<FAQAccordion items={[t94, t95, t96, t97, t98, t99, t100, t101, t102, t103, t104, t105, {
          question: "What do Campus students have to say about their experiences?",
          answer: <>{t106}{t107}<p>Want to hear from more of our students? Read some of our recent{" "}<Link href="/blog/student-life/campus-student-reviews" target="_blank">student reviews</Link>. We hope we'll get to tell your success story someday, too.</p></>
        }]} /></Container></FAQSection>;
    $[107] = t108;
  } else {
    t108 = $[107];
  }
  let t109;
  let t110;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t109 = <h2>Conclusion</h2>;
    t110 = <p>If you're looking to take the next step in your education or career, an online community college program can help you move towards your goals.</p>;
    $[108] = t109;
    $[109] = t110;
  } else {
    t109 = $[108];
    t110 = $[109];
  }
  let t111;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t111 = <a href="tel:888-675-2460">888-675-2460</a>;
    $[110] = t111;
  } else {
    t111 = $[110];
  }
  let t112;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t112 = <div id="wrapper">{t1}{t3}{t45}{t46}{t92}{t108}<Section><Container width="sm"><Prose size="article">{t109}{t110}<p>Come see what Campus is all about by taking a virtual tour or chatting with one of our admissions counselors. We're happy to answer any questions you might have about enrollment, programs, classes, support services, and more. You can call us at {t111} or email us at{" "}<a href="mailto:admissions@campus.edu">admissions@campus.edu</a>{". Let's start your application today."}</p></Prose></Container></Section><CTA surface="subtle" /><ApplyNow /></div>;
    $[111] = t112;
  } else {
    t112 = $[111];
  }
  let t113;
  if ($[112] !== site) {
    t113 = <Layout site={site}>{t112}</Layout>;
    $[112] = site;
    $[113] = t113;
  } else {
    t113 = $[113];
  }
  return t113;
}
export default OnlineCommunityCollege;